import React, { useState, useEffect } from 'react';
import { BsCheckCircleFill, BsXCircleFill, BsExclamationTriangleFill, BsInfoCircleFill } from 'react-icons/bs';

/**
 * Toast Notification Component
 * Shows auto-dismissing notifications at the top-right corner
 * 
 * Usage:
 * const [toast, setToast] = useState(null);
 * const showToast = (message, type = 'success') => setToast({ message, type });
 * <Toast toast={toast} onClose={() => setToast(null)} />
 */
const Toast = ({ toast, onClose, duration = 3000 }) => {
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [toast, onClose, duration]);

  if (!toast) return null;

  const getStyles = () => {
    const baseStyles = 'fixed top-5 right-5 min-w-[300px] max-w-[500px] px-5 py-4 rounded-lg flex items-center gap-3 shadow-2xl z-[9999] animate-slideInRight font-medium text-sm';
    
    switch (toast.type) {
      case 'success':
        return `${baseStyles} bg-green-500 text-white border border-green-600`;
      case 'error':
        return `${baseStyles} bg-red-500 text-white border border-red-600`;
      case 'warning':
        return `${baseStyles} bg-amber-500 text-white border border-amber-600`;
      case 'info':
        return `${baseStyles} bg-blue-500 text-white border border-blue-600`;
      default:
        return `${baseStyles} bg-green-500 text-white border border-green-600`;
    }
  };

  const getIcon = () => {
    const iconProps = { size: 20, className: 'flex-shrink-0' };
    
    switch (toast.type) {
      case 'success':
        return <BsCheckCircleFill {...iconProps} />;
      case 'error':
        return <BsXCircleFill {...iconProps} />;
      case 'warning':
        return <BsExclamationTriangleFill {...iconProps} />;
      case 'info':
        return <BsInfoCircleFill {...iconProps} />;
      default:
        return <BsCheckCircleFill {...iconProps} />;
    }
  };

  return (
    <div className={getStyles()}>
      {getIcon()}
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={onClose}
        className="bg-transparent border-none text-inherit cursor-pointer text-xl leading-none opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
      >
        ×
      </button>
    </div>
  );
};

export default Toast;
